
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';

const MODAL_BREAKPOINT = 990;

/**
 * Shared drawer component.
 *
 * Desktop:
 * - Non-modal drawer
 * - Preserves existing theme layout behaviour
 *
 * Mobile/tablet:
 * - Modal overlay
 * - Backdrop and scroll locking
 *
 * Improvements:
 * - Avoid redundant opening animations
 * - Prevent unwanted scrolling during focus changes
 * - Preserve existing Shopify drawer events
 * - Maintain keyboard and accessibility support
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(
    `(max-width: ${MODAL_BREAKPOINT - 1}px)`
  );

  /**
   * Returns whether this drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#modalQuery.addEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    const restoredOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restores a drawer already opened before JS initialisation.
   */
  #onRestore() {
    const { panel } = this.refs;

    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    unlockScroll(this.refs.panel);

    this.#modalQuery.removeEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    this.#removeEventListeners();

    removeTrapFocus();

    this.#deferredOpen = false;
  }

  /**
   * Backdrop click handling.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (!isClickedOutside(event, panel)) {
      return;
    }

    if (this.#hasOpenNestedDialog()) {
      return;
    }

    this.close();
  };

  /**
   * Checks whether another dialog is open inside
   * this drawer.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.refs.panel.querySelector('dialog[open]')
    );
  }

  /**
   * Handles switching between mobile modal
   * and desktop non-modal modes.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) {
      return;
    }

    const { panel } = this.refs;

    const nestedDialog = this.#getOpenNestedDialog();

    const activeElement = document.activeElement;

    const nestedActiveElement =
      nestedDialog?.contains(activeElement) &&
      activeElement instanceof HTMLElement
        ? activeElement
        : null;

    panel.close();

    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(
        nestedDialog,
        nestedActiveElement
      );
    } else {
      this.#focusInitialElement();
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  };

  /**
   * Handles Escape key.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    if (event.isComposing || event.defaultPrevented) {
      return;
    }

    if (!this.isOpen || !this.#isTopmost()) {
      return;
    }

    event.preventDefault();

    this.close();
  };

  /**
   * Opens the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  open({ enter } = {}) {
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(
      panel,
      () => {
        panel.classList.remove(openClass);
      },
      { subtree: false }
    );

    this.setAttribute('open', '');

    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  /**
   * Brings an already-open drawer to the front.
   *
   * Optimisation:
   * Do not replay the opening animation unnecessarily.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    if (!panel.open) {
      return;
    }

    const wasTopmost = this.#isTopmost();

    if (wasTopmost) {
      return;
    }

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    if (this.#modalQuery.matches) {
      lockScroll(panel);

      panel.close();
      panel.showModal();
    }

    this.#focusInitialElement();

    // Preserve this event for drawer state tracking.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  /**
   * Removes drawer event handlers.
   */
  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.removeEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  /**
   * Focuses the close button without causing
   * unexpected page scrolling.
   */
  #focusInitialElement() {
    const closeButton =
      /** @type {HTMLElement | null} */ (
        this.refs.panel.querySelector(
          '.theme-drawer__close-button'
        )
      );

    closeButton?.focus({
      preventScroll: true,
    });
  }

  /**
   * Preserves nested modal dialogs when the
   * drawer changes between screen modes.
   *
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement | null} focusTarget
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      dialog.contains(focusTarget)
    ) {
      focusTarget.focus({
        preventScroll: true,
      });
    }
  }

  /**
   * Closes the drawer after its exit animation.
   */
  async #closeDialog() {
    if (!this.isOpen || this.#isClosing) {
      return;
    }

    this.#isClosing = true;

    this.#removeEventListeners();

    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');

    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start'
      );

      panel.classList.add(
        'theme-drawer__dialog--closing'
      );

      await onAnimationEnd(
        panel,
        undefined,
        { subtree: false }
      );

      panel.classList.remove(
        'theme-drawer__dialog--closing'
      );
    }

    panel.close();

    this.style.removeProperty(
      '--drawer-stack-order'
    );

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;

    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus({
        preventScroll: true,
      });
    } else {
      const fallback =
        /** @type {HTMLElement | null} */ (
          document.querySelector(
            `[aria-controls="${this.id}"]`
          )
        );

      fallback?.focus({
        preventScroll: true,
      });
    }

    this.#isClosing = false;

    if (this.#deferredOpen) {
      const options = this.#deferredOpen;

      this.#deferredOpen = false;

      this.open(options);
    }
  }

  /**
   * Public close method.
   */
  async close() {
    if (this.#isClosing) {
      this.#deferredOpen = false;
      return;
    }

    await this.#closeDialog();
  }

  /**
   * Toggles the drawer.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }

    return this.open();
  }

  /**
   * Determines whether this drawer is
   * currently the topmost open drawer.
   *
   * @returns {boolean}
   */
  #isTopmost() {
    const myOrder = Number(
      this.style.getPropertyValue(
        '--drawer-stack-order'
      ) || 0
    );

    for (
      const other of document.querySelectorAll(
        'theme-drawer[open]'
      )
    ) {
      if (other === this) {
        continue;
      }

      const otherOrder = Number(
        /** @type {HTMLElement} */ (other)
          .style.getPropertyValue(
            '--drawer-stack-order'
          ) || 0
      );

      if (otherOrder > myOrder) {
        return false;
      }
    }

    return true;
  }
}

/**
 * Register the shared Shopify drawer component.
 */
if (!customElements.get('theme-drawer')) {
  customElements.define(
    'theme-drawer',
    ThemeDrawer
  );
}

/**
 * Fired when a drawer opens.
 */
export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(
      DrawerOpenEvent.eventName,
      { bubbles: true }
    );
  }

  static eventName = 'theme-drawer:open';
}

/**
 * Fired when a drawer closes.
 */
export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(
      DrawerCloseEvent.eventName,
      { bubbles: true }
    );
  }

  static eventName = 'theme-drawer:close';
}
